import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/esm/Button";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "./communitystyles.css";

// Specify workerSrc for pdfjs
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const ZOOM_STEP = 0.2;
const DEFAULT_SCALE = 1.3;
const NAV_BTN_STYLE = { backgroundColor: "#1389e1" };
const RESET_BTN_STYLE = { background: "#36BB7D", border: "none" };
const HIGHLIGHT_BTN_STYLE = { background: "#36BB7D", border: "none" };

const PdfViewer = ({ pdfFile }) => {
	const [numPages, setNumPages] = useState(null);
	const [pageNumber, setPageNumber] = useState(1);
	const [scale, setScale] = useState(DEFAULT_SCALE);
	const [searchTerm, setSearchTerm] = useState("");
	const [highlights, setHighlights] = useState([]);
	const [currentHighlightIndex, setCurrentHighlightIndex] = useState(null);
	const [loading, setLoading] = useState(false);

	const pdfViewerRef = useRef(null);
	const observerRef = useRef(null);

	const increaseZoom = useCallback(
		() => setScale((prev) => prev + ZOOM_STEP),
		[]
	);
	const decreaseZoom = useCallback(
		() => setScale((prev) => (prev > ZOOM_STEP ? prev - ZOOM_STEP : prev)),
		[]
	);
	const resetZoom = useCallback(() => setScale(DEFAULT_SCALE), []);

	const onDocumentLoadSuccess = useCallback(({ numPages }) => {
		setNumPages(numPages);
		setPageNumber(1);
		setSearchTerm("");
		setHighlights([]);
	}, []);

	const searchInPdf = useCallback(
		async (term) => {
			setLoading(true);
			if (searchTerm === "") setHighlights([]);
			const highlightPositions = [];
			const loadingTask = pdfjs.getDocument(pdfFile);
			const pdf = await loadingTask.promise;
			for (let i = 1; i <= pdf.numPages; i++) {
				const page = await pdf.getPage(i);
				const viewport = page.getViewport({ scale });
				const textContent = await page.getTextContent();
				textContent.items.forEach((item) => {
					const text = item.str;
					const [x, y, itemWidth, itemHeight] = [
						item.transform[4],
						item.transform[5],
						item.width,
						item.height,
					];
					let searchIndex = text.toLowerCase().indexOf(term.toLowerCase());
					while (searchIndex !== -1) {
						const charWidth = itemWidth / text.length;
						const highlightX = x + searchIndex * charWidth;
						const highlightWidth = charWidth * term.length;
						const highlightY = viewport.height * 0.77 - y - itemHeight;
						highlightPositions.push({
							page: i,
							x: highlightX,
							y: highlightY,
							width: highlightWidth,
							height: itemHeight,
						});
						searchIndex = text
							.toLowerCase()
							.indexOf(term.toLowerCase(), searchIndex + term.length);
					}
				});
			}
			setHighlights(highlightPositions);
			setLoading(false);
			if (highlightPositions.length > 0) {
				setCurrentHighlightIndex(0);
				setPageNumber(highlightPositions[0].page);
				setTimeout(() => {
					const highlightElement = document.querySelector(
						`[data-highlight-index='0']`
					);
					if (highlightElement) {
						highlightElement.scrollIntoView({
							behavior: "smooth",
							block: "center",
						});
					}
				}, 500);
			}
		},
		[pdfFile, scale, searchTerm]
	);

	useEffect(() => {
		if (observerRef.current) observerRef.current.disconnect();
		observerRef.current = new MutationObserver((mutations) => {
			mutations.forEach((mutation) => {
				if (mutation.type === "childList" && mutation.addedNodes.length) {
					mutation.addedNodes.forEach((node) => {
						if (node.nodeName === "A") node.setAttribute("target", "_blank");
						if (node.querySelectorAll) {
							node
								.querySelectorAll("a")
								.forEach((link) => link.setAttribute("target", "_blank"));
						}
					});
				}
			});
		});
		const annotationLayers = document.querySelectorAll(
			".react-pdf__Page__annotations"
		);
		annotationLayers.forEach((layer) => {
			observerRef.current.observe(layer, { childList: true, subtree: true });
		});
		return () => {
			if (observerRef.current) observerRef.current.disconnect();
		};
	}, [pageNumber, numPages]);

	const handleSearch = useCallback(
		(event) => {
			event.preventDefault();
			if (searchTerm === "") {
				setHighlights([]);
				return;
			}
			searchInPdf(searchTerm);
		},
		[searchTerm, searchInPdf]
	);

	const handleNextHighlight = useCallback(() => {
		if (highlights.length === 0 || currentHighlightIndex === null) return;
		const nextIndex = (currentHighlightIndex + 1) % highlights.length;
		setCurrentHighlightIndex(nextIndex);
		setPageNumber(highlights[nextIndex].page);
		setTimeout(() => {
			const highlightElement = document.querySelector(
				`[data-highlight-index='${nextIndex}']`
			);
			if (highlightElement) {
				highlightElement.scrollIntoView({
					behavior: "smooth",
					block: "center",
				});
			}
		}, 500);
	}, [highlights, currentHighlightIndex]);

	const handlePreviousHighlight = useCallback(() => {
		if (highlights.length === 0 || currentHighlightIndex === null) return;
		const prevIndex =
			(currentHighlightIndex - 1 + highlights.length) % highlights.length;
		setCurrentHighlightIndex(prevIndex);
		setPageNumber(highlights[prevIndex].page);
		setTimeout(() => {
			const highlightElement = document.querySelector(
				`[data-highlight-index='${prevIndex}']`
			);
			if (highlightElement) {
				highlightElement.scrollIntoView({
					behavior: "smooth",
					block: "center",
				});
			}
		}, 500);
	}, [highlights, currentHighlightIndex]);

	const handleNextPage = useCallback(() => {
		if (pageNumber < numPages) setPageNumber((prev) => prev + 1);
	}, [pageNumber, numPages]);

	const handlePreviousPage = useCallback(() => {
		if (pageNumber > 1) setPageNumber((prev) => prev - 1);
	}, [pageNumber]);

	const handleLinkClick = useCallback((e) => {
		e.preventDefault();
		const link = e.target.closest("a");
		if (link) window.open(link.href, "_blank");
	}, []);

	const onRenderAnnotationLayerSuccess = useCallback(() => {
		const links = document.querySelectorAll(".react-pdf__Page__annotations a");
		links.forEach((link) => {
			link.addEventListener("click", handleLinkClick);
		});
	}, [handleLinkClick]);

	return (
		<div
			className="pdf-container"
			id="pdfviewer"
			ref={pdfViewerRef}
			onContextMenu={(e) => e.preventDefault()}>
			<div
				style={{
					display: "flex",
					position: "fixed",
					bottom: "10px",
					right: "10px",
					zIndex: 99,
				}}>
				<button
					className="btn btn-sm"
					style={NAV_BTN_STYLE}
					onClick={decreaseZoom}>
					<i className="fas fa-search-minus"></i>
				</button>
				&nbsp;
				{scale !== DEFAULT_SCALE && (
					<Button
						onClick={resetZoom}
						style={RESET_BTN_STYLE}
						className="highlight-nav-button">
						Reset
					</Button>
				)}
				&nbsp;
				<button
					className="btn btn-sm"
					style={NAV_BTN_STYLE}
					onClick={increaseZoom}>
					<i className="fas fa-search-plus"></i>
				</button>
			</div>
			<div className="navigation-controls">
				<Button
					onClick={handlePreviousPage}
					disabled={pageNumber <= 1}
					variant="success"
					className="nav-button btn btn-sm">
					<FaArrowLeft /> Previous Page
				</Button>
				<form onSubmit={handleSearch} style={{ margin: "20px 0" }}>
					{highlights.length > 0 && (
						<Button
							onClick={handlePreviousHighlight}
							disabled={
								currentHighlightIndex === null || highlights.length === 0
							}
							style={HIGHLIGHT_BTN_STYLE}
							className="highlight-nav-button">
							<FaArrowLeft />
						</Button>
					)}
					<input
						type="text"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						placeholder="Search in PDF"
						className="form-control-sm"
					/>
					&nbsp;
					<button type="submit" className="btn btn-sm" style={NAV_BTN_STYLE}>
						<i className="fas fa-search"></i>
					</button>
					{highlights.length > 0 && (
						<Button
							onClick={handleNextHighlight}
							disabled={
								currentHighlightIndex === null || highlights.length === 0
							}
							style={HIGHLIGHT_BTN_STYLE}
							className="highlight-nav-button">
							<FaArrowRight />
						</Button>
					)}
				</form>
				<Button
					onClick={handleNextPage}
					disabled={pageNumber >= numPages}
					variant="success"
					className="nav-button btn btn-sm">
					Next Page <FaArrowRight />
				</Button>
			</div>
			<div className="page-info">
				Page {pageNumber} of {numPages}
			</div>
			{loading && (
				<div className="loading-indicator">
					<div className="spinner-border" role="status">
						<span className="sr-only">Loading...</span>
					</div>
				</div>
			)}
			<Document
				file={pdfFile}
				loading={
					<div className="parentDisable" width="100%">
						<div className="overlay-box">
							<div className="spinner-border" role="status">
								<span className="sr-only">Loading...</span>
							</div>
						</div>
					</div>
				}
				onLoadSuccess={onDocumentLoadSuccess}>
				<Page
					pageNumber={pageNumber}
					scale={scale}
					onRenderAnnotationLayerSuccess={onRenderAnnotationLayerSuccess}>
					{highlights
						.filter((highlight) => highlight.page === pageNumber)
						.map((highlight, index) => (
							<div
								key={index}
								className="highlight"
								data-highlight-index={index}
								style={{
									position: "absolute",
									left: `${highlight.x * scale}px`,
									top: `${highlight.y * scale}px`,
									width: `${highlight.width * scale}px`,
									height: `${highlight.height * scale}px`,
									backgroundColor: "yellow",
									opacity: 0.4,
									pointerEvents: "none",
								}}></div>
						))}
				</Page>
			</Document>
		</div>
	);
};

export default PdfViewer;
